import { Link, useParams } from "react-router-dom";
import { MapPin, Quote, ShieldCheck } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee, SpotlightCard } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

export default function SectorCity() {
  const { sector, city } = useParams();
  const { data, loading, error } = useApi(() => api.sectorCity(sector, city), [sector, city]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="sector-city-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Page not found</p>
          <Link to="/sectors" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            View all sectors
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const s = data.sector;
  const c = data.city;
  const marqueeItems = [
    ...data.sections[0].services.map((sv) => `${sv.name} ${c.name}`),
    ...c.areas.map((a) => `${s.name} Security ${a}`),
  ];

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/sectors/${sector}/${city}`} schema={faqSchema(data.faqs)} image={data.image} keywords={data.keywords} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} image={data.image} />
      <Marquee items={marqueeItems} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets}>
          {sec.services && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sector-city-services-grid">
              {sec.services.map((sv) => (
                <Link key={sv.slug} to={`/locations/${city}/${sv.slug}`} data-testid={`sector-city-service-${sv.slug}`} className="block h-full group">
                  <SpotlightCard className="h-full p-5 hover:border-amber/60 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-amber shrink-0" />
                      <span className="font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors leading-tight">
                        {sv.name} {c.name}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 leading-relaxed">{sv.short}</p>
                  </SpotlightCard>
                </Link>
              ))}
            </div>
          )}
        </SectionBlock>
      ))}

      {data.cases.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="sector-city-cases">
          <ChapterTag num="PROOF" title={`${s.name.toUpperCase()} — CLIENT OUTCOMES`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.cases.map((cs, i) => (
              <SpotlightCard key={i} className="p-6 sm:p-8 hover:border-amber/60 transition-colors duration-300" testId={`sector-city-case-${i}`}>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  <MapPin className="w-3 h-3 text-amber" /> {cs.location} — {cs.client}
                </div>
                <h3 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-amber leading-tight">
                  {cs.headline}
                </h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{cs.body}</p>
                <div className="mt-5 border-l-2 border-amber pl-4">
                  <Quote className="w-4 h-4 text-amber mb-2" />
                  <p className="text-sm text-gray-300 italic leading-relaxed">“{cs.quote}”</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">{cs.author}</p>
                </div>
              </SpotlightCard>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-gray-600">
            Representative client engagements — details anonymised
          </p>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="sector-city-faq-section">
        <ChapterTag num="FAQ" title={`${s.name.toUpperCase()} SECURITY ${c.name.toUpperCase()} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="sector-city-faq" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 space-y-12">
        <div>
          <ChapterTag num="GRID" title={`${s.name.toUpperCase()} SECURITY IN NEARBY LOCATIONS`} />
          <LinkPills
            items={data.nearby}
            toFn={(nb) => `/sectors/${sector}/${nb.slug}`}
            labelFn={(nb) => `${s.name} Security ${nb.name}`}
            testIdPrefix="sector-city-nearby"
          />
        </div>
        <div>
          <ChapterTag num="OPS" title={`MORE SECURITY IN ${c.name.toUpperCase()}`} />
          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to={`/locations/${city}`}
              data-testid="sector-city-all-services"
              className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-amber/40 bg-card text-amber hover:bg-amber hover:text-ink transition-colors duration-300"
            >
              All Security Services {c.name}
            </Link>
            <Link
              to={`/sectors/${sector}`}
              data-testid="sector-city-sector-hub"
              className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-white/10 bg-card text-gray-300 hover:border-amber hover:text-amber transition-colors duration-300"
            >
              {s.name} Security UK-Wide
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
